import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import logo from "../../../../src/assets/img/uniexpo.webp";
import teenager from "../../../../src/assets/img/teenager.webp";
import { Facebook, Instagram, Linkedin, Arrow } from '../../../assets/img/svg/icons';

interface NavigationLinkProps {
    to: string;
    name: string;
    mouseX: number;
}

const NavigationLink: React.FC<NavigationLinkProps> = ({ to, name, mouseX }) => {
    const location = useLocation();
    const isActive = location.pathname === `/${to}` || (to === '' && location.pathname === '/');

    return (
        <a href={`/${to}`} className={`transition-all duration-200 hover:text-main-pink inline-block h-full relative group ${isActive ? 'text-main-yellow lg:text-main-pink' : 'text-white lg:text-black' }`}>
            {name}

            <span className={`fixed w-20 h-20 -top-20 left-0 z-10 bg-main-pink rounded-full pointer-events-none transition-all duration-300 ease-out group-hover:opacity-100 group-hover:-top-14 hidden lg:block 
                ${isActive
                    ? 'opacity-100 -top-24 left-1/2 absolute group-hover:-top-24'
                    : 'opacity-0 -top-20 left-0 fixed'
                }`}
                style={isActive ? { transform: 'translateX(-50%)' } : { transform: `translateX(${mouseX - 40}px)` }}
            />
        </a>
    );
};

const Header: React.FC = () => {

    const [isOpen, setIsOpen] = useState(false);

    const toggleMenu = () => {
        setIsOpen(!isOpen);
    };

    const [mouseX, setMouseX] = useState(0);

    useEffect(() => {
        const handleMouseMove = (e: MouseEvent) => {
            setMouseX(e.clientX);
        };

        document.addEventListener('mousemove', handleMouseMove);
        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
        };
    }, []);

    return (
        <header className={`py-4 lg:shadow-sm transition-all duration-200 ${isOpen ? 'bg-main-blue lg:bg-white' : 'bg-white'}`}>
            <div className="max-w-screen-xl 2xl:max-w-screen-2xl mx-auto px-4">
                <div className="flex items-end lg:items-center flex-col lg:flex-row justify-between lg:gap-8">
                    <div className="flex items-center justify-between lg:justify-normal gap-2 lg:gap-8 w-full lg:w-auto">
                        <div className="flex items-center gap-2 shrink-0 order-1 lg:order-none z-50">
                            <a href="https://www.facebook.com/uniexpooficial/" target="_blank" className="bg-main-yellow rounded-full h-8 w-8 inline-flex items-center justify-center transition-all duration-200 hover:bg-[#3b5998]" title="Facebook">
                                <Facebook className="h-1/2 object-contain w-1/2" />
                            </a>

                            <a href="https://www.instagram.com/uniexpo_oficial/" target="_blank" className="overflow-hidden relative bg-main-yellow rounded-full h-8 w-8 inline-flex items-center justify-center transition-all duration-200 before:content-[''] before:absolute before:inset-0 before:bg-[radial-gradient(circle_at_33%_100%,_#fed373_4%,_#f15245_30%,_#d92e7f_62%,_#9b36b7_85%,_#515ecf)] before:-z-10 before:opacity-0 before:transition-all before:duration-300 hover:before:opacity-100 hover:bg-transparent" title="Instagram">
                                <Instagram className="h-1/2 object-contain w-1/2" />
                            </a>

                            <a href="https://br.linkedin.com/company/teenagerassessoria" target="_blank" className="bg-main-yellow rounded-full h-8 w-8 inline-flex items-center justify-center transition-all duration-200 hover:bg-[#0077b5]" title="Linkedin">
                                <Linkedin className="h-1/2 object-contain w-1/2" />
                            </a>

                            <div className={`order-2 justify-self-end shrink-0 rounded-full inline-flex items-center justify-center lg:hidden h-10 w-10 bg-main-yellow transition-all duration-200 z-50 ${isOpen ? 'bg-transparent' : ''}`} onClick={toggleMenu}>
                                <span className={`bg-white h-1 w-6 inline-block rounded-sm relative transition-all duration-200 before:content-[''] before:w-6 before:h-1 before:bg-white before:rounded-sm before:-translate-y-2 before:absolute after:content-[''] after:w-6 after:h-1 after:bg-white after:rounded-sm after:translate-y-2 after:absolute ${isOpen ? 'rotate-45 before:translate-y-0 before:rotate-90 after:hidden' : ''}`}></span>
                            </div>
                        </div>

                        <a href="/" className={`inline-block shrink-0 z-50 relative before:bg-white before:w-[110px] before:h-[110px] before:-translate-x-[7px] before:-z-10 before:content-[""] before:transition-all before:duration-200 before:absolute before:left-0 before:top-0 before:rounded-b-full before:block lg:before:hidden ${isOpen ? 'before:-translate-y-[23px]' : 'before:-translate-y-[43px]'}`} title='Uniexpo'>
                            <img src={logo} alt="Logo Uniexpo" className='max-w-24 w-full h-auto' />
                            <h1 className='overflow-hidden max-h-0'>Uniexpo</h1>
                        </a>
                    </div>

                    <nav className={`flex items-end lg:items-center flex-col lg:flex-row gap-2 justify-between w-full text-right lg:text-center text-sm font-semibold shrink-[20] lg:max-h-none overflow-hidden lg:overflow-visible transition-all duration-300 z-40 ${isOpen ? 'max-h-72 max-w-full lg:max-w-[1100px] opacity-100 lg:relative' : 'max-h-0 max-w-0 opacity-0 lg:opacity-100 lg:max-w-[1100px] lg:max-h-none'}`}>
                        <span className={`bg-main-pink top-0 right-0 w-dvw h-[22rem] absolute block lg:hidden rounded-bl-full -z-10 overflow-hidden transition-all duration-100 ${isOpen ? 'max-h-96 max-w-full' : 'max-h-0 max-w-0'}`}></span>

                        <NavigationLink to="agenda-de-eventos" name="Agenda de Eventos" mouseX={mouseX} />
                        <NavigationLink to="quem-somos" name="Quem Somos" mouseX={mouseX} />
                        <NavigationLink to="seja-um-expositor" name="Seja um Expositor" mouseX={mouseX} />
                        <NavigationLink to="uniexpo-no-seu-colegio" name="Uniexpo no seu Colégio" mouseX={mouseX} />
                        <NavigationLink to="blog" name="Blog" mouseX={mouseX} />
                        <NavigationLink to="contatos" name="Contatos" mouseX={mouseX} />

                        <a href="https://teenager.com.br/" target="_blank" title='Teenager' className="group overflow-hidden relative flex gap-3 items-center bg-[#E7E7FD] rounded-3xl py-1.5 px-2.5 border-2 border-transparent transition-all duration-300 shrink-0">
                            <img src={teenager} alt="Teenager logo" className="h-7 w-auto object-contain transition-all duration-300 group-hover:translate-x-full group-hover:scale-125" />
                            <span className="transition-all duration-300 group-hover:opacity-0 group-hover:translate-x-full">Acessar</span>
                            <span className="h-8 w-8 bg-white rounded-full inline-flex items-center justify-center transition-all duration-300 group-hover:opacity-0 group-hover:translate-x-full">
                                <Arrow className="h-1/2 object-contain w-1/2" />
                            </span>
                        </a>
                    </nav>
                </div>
            </div>
        </header>
    );
};

export default Header;
